import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, Mail, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';

/**
 * Bare-email people attached to a client account who receive that account's
 * menu announcements (with the account's pricing) but hold no portal login.
 *
 * One component, two surfaces: the client manages their own list in Profile,
 * an admin manages it on their behalf from the client detail dialog. The
 * caller injects the four API calls; everything else is identical.
 */
export const AnnouncementContactsCard = ({ api, canEdit = true, testIdPrefix = 'announcement-contacts' }) => {
  const [contacts, setContacts] = useState([]);
  const [maxContacts, setMaxContacts] = useState(8);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', label: '' });
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list();
      setContacts(data.contacts || []);
      if (data.max_contacts) setMaxContacts(data.max_contacts);
    } catch {
      toast.error('Failed to load menu notification contacts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.email.trim() || !form.label.trim()) {
      toast.error('Both an email address and a label are required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      await api.add({ email: form.email.trim(), label: form.label.trim() });
      toast.success(`${form.email.trim()} will now receive menu announcements`);
      setForm({ email: '', label: '' });
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to add contact'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (contact) => {
    setBusyId(contact.id);
    try {
      await api.update(contact.id, { active: !(contact.active ?? true) });
      toast.success(contact.active ? 'Contact muted' : 'Contact unmuted');
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update contact'));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (contact) => {
    if (!window.confirm(`Stop sending menu announcements to ${contact.email}?`)) return;
    setBusyId(contact.id);
    try {
      await api.remove(contact.id);
      toast.success('Contact removed');
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to remove contact'));
    } finally {
      setBusyId(null);
    }
  };

  const atCap = contacts.length >= maxContacts;

  return (
    <Card data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-500" />
            Menu notification contacts
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Extra email addresses that receive every new-menu announcement — with this
            account&apos;s pricing — without needing a portal login. Useful for a shift
            manager or a buyer who orders on your behalf.
          </p>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 self-start"
            disabled={atCap}
            onClick={() => setShowAdd(true)}
            data-testid={`${testIdPrefix}-add-btn`}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add contact
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8" data-testid={`${testIdPrefix}-empty`}>
            <Mail className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No extra menu contacts yet</p>
          </div>
        ) : (
          <div className="border rounded-lg divide-y">
            {contacts.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 p-3"
                data-testid={`${testIdPrefix}-row-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">{c.label}</span>
                    {!(c.active ?? true) && (
                      <Badge variant="outline" className="text-gray-500" data-testid={`${testIdPrefix}-muted-${c.id}`}>
                        Muted
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{c.email}</p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      title={c.active ?? true ? 'Mute (stop sending)' : 'Unmute'}
                      disabled={busyId === c.id}
                      onClick={() => handleToggle(c)}
                      data-testid={`${testIdPrefix}-toggle-${c.id}`}
                    >
                      {c.active ?? true
                        ? <Bell className="h-4 w-4 text-amber-600" />
                        : <BellOff className="h-4 w-4 text-gray-400" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600"
                      title="Remove"
                      disabled={busyId === c.id}
                      onClick={() => handleRemove(c)}
                      data-testid={`${testIdPrefix}-remove-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!loading && (
          <p className="text-xs text-gray-400 mt-3" data-testid={`${testIdPrefix}-cap`}>
            {contacts.length} of {maxContacts} contacts used
            {atCap && ' — remove one before adding another'}
          </p>
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-add-dialog`}>
          <DialogHeader>
            <DialogTitle>Add a menu notification contact</DialogTitle>
            <DialogDescription>
              They&apos;ll receive the same announcement email as the account — product images
              and this account&apos;s pricing. No login is created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor={`${testIdPrefix}-email`}>Email address *</Label>
              <Input
                id={`${testIdPrefix}-email`}
                type="email"
                value={form.email}
                placeholder="erin@riversidecafe.com"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid={`${testIdPrefix}-email-input`}
              />
            </div>
            <div>
              <Label htmlFor={`${testIdPrefix}-label`}>Label *</Label>
              <Input
                id={`${testIdPrefix}-label`}
                value={form.label}
                placeholder="Erin — AM manager"
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid={`${testIdPrefix}-label-input`}
              />
              <p className="text-xs text-gray-400 mt-1">
                So you can tell contacts apart later. The email address can&apos;t be edited
                afterwards — remove and re-add instead.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleAdd}
              disabled={submitting}
              data-testid={`${testIdPrefix}-submit-btn`}
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AnnouncementContactsCard;
